const { isFunction } = require('../type');

const targetPropName = 'actionExecutionsBeforeRedirection';

/**
 * Retrieves the list of action executions from the event's target.
 * @param {Event} event - The event containing the target.
 * @returns {Promise<*>[]} An array of promises representing
 * action executions.
 */
const getActionExecutions = (event) => event.target[targetPropName];

/**
 * Initializes the action executions array on the event's target.
 * Ensures the target property exists and is an array.
 * @param {Event} event - The event whose target will be initialized.
 */
const initActionExecutions = (event) => {
    const { target } = event;
    target[targetPropName] = Array.isArray(target[targetPropName])
        ? target[targetPropName]
        : [];
};

/**
 * Pushes a new action execution promise into the event's
 * action executions array.
 * @param {Event} event - The event containing the executions array.
 * @param {Promise<*>} execution - A promise representing an
 * action execution.
 */
const pushActionExecution = (event, execution) => {
    getActionExecutions(event).push(execution);
};

/**
 * Determines whether the clicked link should be processed.
 * @param {HTMLAnchorElement} link - The clicked link element.
 * @param {Event} event - The click event.
 * @returns {boolean} True if the link should be processed.
 */
function shouldProcessLink(link, event) {
    if (!link?.href) return false;

    const isSameOrigin = link.origin === window.location.origin;
    const isUserModifiedClick = event.ctrlKey || event.metaKey;
    const opensInNewTab = link.target === '_blank';

    return isSameOrigin && !isUserModifiedClick && !opensInNewTab;
}

/**
 * Default navigation behavior if no custom navigate function
 * is provided.
 * @param {Object} options - Navigation parameters.
 * @param {Element} options.linkElement - The clicked anchor element.
 */
function defaultNavigate({ linkElement }) {
    window.location.href = linkElement.href;
}

/**
 * Default behavior to prevent default link navigation.
 * @param {Object} options - Context for the prevention.
 * @param {Event} options.clickEvent - The original click event.
 */
function defaultPreventDefault({ clickEvent }) {
    clickEvent.preventDefault();
}

/**
 * Promise all registered actions have completed.
 * Logs any rejected promises before resolving.
 * @param {Promise<*>[]} handlers - The promises to wait for.
 * @returns {Promise<void>} Resolves after all handlers settle.
 */
function whenAllActionExecutionsSettled(handlers) {
    return new Promise(resolve => {
        queueMicrotask(() => {
            Promise.allSettled(handlers).then(results => {
                const failed = results.filter(r => r.status === 'rejected');
                if (failed.length > 0) {
                    console.warn(
                        'Algunas acciones fallaron antes del redireccionamiento:',
                        failed.map(f => f.reason)
                    );
                }
                resolve();
            });
        });
    });
}

/**
 * Extracts the anchor element from a click event.
 * @param {Event} clickEvent - The click event.
 * @returns {HTMLAnchorElement|null} The closest anchor element.
 */
function getLinkElementFromClick(clickEvent) {
    return clickEvent.target.closest('a');
}

/**
 * Prepares and stores the execution promise for a link click.
 * @param {Event} clickEvent - The original click event.
 * @param {Element} linkElement - The anchor element.
 * @param {Function} actionBeforeRedirect - Callback to run before redirect.
 * @returns {Promise<*>[]} The list of execution promises.
 */
function prepareActionExecution(
    clickEvent,
    linkElement,
    actionBeforeRedirect
) {
    initActionExecutions(clickEvent);
    const actionExecution = Promise.resolve(
        actionBeforeRedirect(clickEvent, { linkElement })
    );
    pushActionExecution(clickEvent, actionExecution);
    return getActionExecutions(clickEvent);
}

/**
 * Creates a click handler function with dependencies injected.
 * @param {Function} actionBeforeRedirect - Action to run before redirect.
 * @param {Function} navigate - Navigation function.
 * @param {Function} preventDefault - Function to prevent default click.
 * @returns {Function} Event handler function.
 */
function handleLinkClickFactory(
    actionBeforeRedirect,
    navigate,
    preventDefault
) {
    return function handleLinkClick(clickEvent) {
        const linkElement = getLinkElementFromClick(clickEvent);
        if (!shouldProcessLink(linkElement, clickEvent)) return;

        preventDefault({ linkElement, clickEvent });

        const executions = prepareActionExecution(
            clickEvent,
            linkElement,
            actionBeforeRedirect
        );

        if (executions.length === 1) {
            whenAllActionExecutionsSettled(executions).then(() => {
                navigate({ linkElement, clickEvent });
            });
        }
    };
}

/**
 * Creates a handler to run actions before navigating to a link.
 * @param {Function} actionBeforeRedirect - Function to run before redirect.
 * @param {Object} [options] - Optional configuration.
 * @param {Function} [options.navigate] - Optional custom navigate function.
 * @param {Function} [options.preventDefault] - Optional prevent default.
 * @returns {Function} A function to handle the click event.
 */
function createLinkHandlerBeforeRedirect(
    actionBeforeRedirect,
    options = {}
) {
    const navigate = isFunction(options.navigate)
        ? options.navigate
        : defaultNavigate;

    const preventDefault = isFunction(options.preventDefault)
        ? options.preventDefault
        : defaultPreventDefault;

    return handleLinkClickFactory(
        actionBeforeRedirect,
        navigate,
        preventDefault
    );
}

module.exports = createLinkHandlerBeforeRedirect;
